import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import { formatDate } from "@/lib/dates";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit, Check, X, AlertCircle } from "lucide-react";
import VehicleSetupEditModal from "./VehicleSetupEditModal";

type Vehicle = {
  vehicle_number: string;
  make: string | null;
  model: string | null;
  year: number | null;
  vehicle_type: string | null;
};


type SetupRecord = {
  id: string;
  vehicle_number: string;
  usage_change: string;
  initial_cof: string;
  fuel_type: string;
  fleet_details: string;
  airport_barrier: string;
  seats_added_taxicaller: boolean;
  tmv_added_taxicaller: boolean;
  non_van_added_taxicaller: boolean;
  car_seat_added_taxicaller: boolean;
  screen_added_taxicaller: boolean;
  tgb_vehicle_added_taxicaller: boolean;
  psl_holder_required: string;
  psl_holder_progress: string;
  loading_certificate_required: string;
  loading_certificate_progress: string;
  kiddilock_signs_required: string;
  kiddilock_signs_progress: string;
  fleet_number_required: string;
  fleet_number_progress: string;
  door_sign_required: string;
  door_sign_progress: string;
  fleet_numbers_lr_required: string;
  fleet_numbers_lr_progress: string;
  fleet_number_rear_required: string;
  fleet_number_rear_progress: string;
  taxi_stand_coupon_required: string;
  taxi_stand_coupon_progress: string;
  roof_rack_required: string;
  roof_rack_progress: string;
  top_light_required: string;
  top_light_progress: string;
  camera_required: string;
  camera_progress: string;
  hardwire_usb_required: string;
  hardwire_usb_progress: string;
  eftpos_machine_required: string;
  eftpos_machine_progress: string;
  tablet_holder_required: string;
  tablet_holder_progress: string;
  eftpos_charger_required: string;
  eftpos_charger_progress: string;
  driver_seat_inspection1: string;
  driver_seat_inspection2: string;
  front_passenger_inspection1: string;
  front_passenger_inspection2: string;
  rear_seats_inspection1: string;
  rear_seats_inspection2: string;
  interior_inspection1: string;
  interior_inspection2: string;
  completed_by: string;
  completed_date: string | null;
};

export default function NewVehicleSetupTab({ isAdmin }: { isAdmin: boolean }) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [setupRecords, setSetupRecords] = useState<Record<string, SetupRecord>>({});
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const loadVehicles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vehicles")
      .select("vehicle_number,make,model,year,vehicle_type")
      .order("vehicle_number");

    if (error) {
      toast({
        title: "Error loading vehicles",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    setVehicles(data ?? []);
    setLoading(false);
  };


  const loadSetupRecords = async () => {
    const { data, error } = await supabase
      .from("vehicle_setup_records")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading setup records:", error);
      return;
    }

    // Create a map of vehicle_number to most recent setup record
    const recordMap: Record<string, SetupRecord> = {};
    for (const record of data ?? []) {
      if (!recordMap[record.vehicle_number]) {
        recordMap[record.vehicle_number] = record;
      }
    }
    setSetupRecords(recordMap);
  };

  useEffect(() => {
    loadVehicles();
    loadSetupRecords();
  }, []);

  const handleEditVehicle = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingVehicle(null);
  };

  const handleSetupSaved = () => {
    loadSetupRecords();
  };

  // Calculate setup status for a vehicle
  const getSetupStatus = (vehicleNumber: string) => {
    const record = setupRecords[vehicleNumber];
    if (!record) {
      return { hasSetup: false, profilesComplete: false, signageComplete: false, hardwareComplete: false, inspectionComplete: false };
    }

    const profilesComplete = 
      record.seats_added_taxicaller &&
      record.tmv_added_taxicaller &&
      record.non_van_added_taxicaller &&
      record.car_seat_added_taxicaller &&
      record.screen_added_taxicaller &&
      record.tgb_vehicle_added_taxicaller;

    const signageItems = [
      { req: record.psl_holder_required, prog: record.psl_holder_progress },
      { req: record.loading_certificate_required, prog: record.loading_certificate_progress },
      { req: record.kiddilock_signs_required, prog: record.kiddilock_signs_progress },
      { req: record.fleet_number_required, prog: record.fleet_number_progress },
      { req: record.door_sign_required, prog: record.door_sign_progress },
      { req: record.fleet_numbers_lr_required, prog: record.fleet_numbers_lr_progress },
      { req: record.fleet_number_rear_required, prog: record.fleet_number_rear_progress },
      { req: record.taxi_stand_coupon_required, prog: record.taxi_stand_coupon_progress },
    ];
    const signageComplete = signageItems.every((item) => item.req === "n/a" || item.prog === "completed");

    const hardwareItems = [
      { req: record.roof_rack_required, prog: record.roof_rack_progress },
      { req: record.top_light_required, prog: record.top_light_progress },
      { req: record.camera_required, prog: record.camera_progress },
      { req: record.hardwire_usb_required, prog: record.hardwire_usb_progress },
      { req: record.eftpos_machine_required, prog: record.eftpos_machine_progress },
      { req: record.tablet_holder_required, prog: record.tablet_holder_progress },
      { req: record.eftpos_charger_required, prog: record.eftpos_charger_progress },
    ];
    const hardwareComplete = hardwareItems.every((item) => item.req === "n/a" || item.prog === "completed");

    const inspections = [
      { i1: record.driver_seat_inspection1, i2: record.driver_seat_inspection2 },
      { i1: record.front_passenger_inspection1, i2: record.front_passenger_inspection2 },
      { i1: record.rear_seats_inspection1, i2: record.rear_seats_inspection2 },
      { i1: record.interior_inspection1, i2: record.interior_inspection2 },
    ];
    const inspectionComplete = inspections.every((insp) => {
      if (insp.i1 === "pass") return true;
      if (insp.i1 === "rectify" && insp.i2 === "pass") return true;
      return false;
    });

    return { hasSetup: true, profilesComplete, signageComplete, hardwareComplete, inspectionComplete };
  };

  const StatusIcon = ({ complete }: { complete: boolean }) => (
    complete 
      ? <Check className="w-4 h-4 text-green-500" />
      : <X className="w-4 h-4 text-red-400" />
  );
  // Show all vehicles
  const activeVehicles = vehicles;


  // Summary stats
  const summary = useMemo(() => {
    let noSetup = 0;
    let complete = 0;
    let incomplete = 0;

    for (const vehicle of activeVehicles) {
      const status = getSetupStatus(vehicle.vehicle_number);
      if (!status.hasSetup) {
        noSetup++;
      } else if (status.profilesComplete && status.signageComplete && status.hardwareComplete && status.inspectionComplete) {
        complete++;
      } else {
        incomplete++;
      }
    }

    return { noSetup, complete, incomplete, total: activeVehicles.length };
  }, [activeVehicles, setupRecords]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-amber-500">New Vehicle Setup</h2>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{summary.total}</div>
            <div className="text-sm text-gray-400">Total Vehicles</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-green-700/50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-500">{summary.complete}</div>
            <div className="text-sm text-gray-400">Setup Complete</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-yellow-700/50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-500">{summary.incomplete}</div>
            <div className="text-sm text-gray-400">In Progress</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-red-700/50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-400">{summary.noSetup}</div>
            <div className="text-sm text-gray-400">No Setup Record</div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicles List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-amber-500">Vehicles ({activeVehicles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading vehicles...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Setup Status</TableHead>
                  <TableHead className="text-center">Profiles</TableHead>
                  <TableHead className="text-center">Signage</TableHead>
                  <TableHead className="text-center">Hardware</TableHead>
                  <TableHead className="text-center">Inspection</TableHead>
                  <TableHead className="text-center">Completed By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeVehicles.map((vehicle) => {
                  const status = getSetupStatus(vehicle.vehicle_number);
                  const record = setupRecords[vehicle.vehicle_number];
                  const allComplete = status.hasSetup && status.profilesComplete && status.signageComplete && status.hardwareComplete && status.inspectionComplete;

                  return (
                    <TableRow key={vehicle.vehicle_number}>
                      <TableCell>
                        <div>
                          <span className="font-medium text-amber-500">{vehicle.vehicle_number}</span>
                          <div className="text-xs text-gray-400">
                            {vehicle.make} {vehicle.model} ({vehicle.year})
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {vehicle.vehicle_type || "N/A"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {!status.hasSetup ? (
                          <Badge variant="secondary" className="bg-red-900/30 text-red-400">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No Setup
                          </Badge>
                        ) : allComplete ? (
                          <Badge variant="secondary" className="bg-green-900/30 text-green-400">
                            <Check className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-900/30 text-yellow-400">
                            In Progress
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {status.hasSetup ? <StatusIcon complete={status.profilesComplete} /> : <span className="text-gray-500">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {status.hasSetup ? <StatusIcon complete={status.signageComplete} /> : <span className="text-gray-500">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {status.hasSetup ? <StatusIcon complete={status.hardwareComplete} /> : <span className="text-gray-500">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {status.hasSetup ? <StatusIcon complete={status.inspectionComplete} /> : <span className="text-gray-500">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {record?.completed_by ? (
                          <div className="text-xs">
                            <div className="text-gray-300">{record.completed_by}</div>
                            {record.completed_date && (
                              <div className="text-gray-500">{formatDate(record.completed_date)}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditVehicle(vehicle)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {status.hasSetup ? "Edit" : "Setup"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <VehicleSetupEditModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        vehicle={editingVehicle}
        onSaved={handleSetupSaved}
      />
    </div>
  );
}
